<template>
  <SoAdminPage
    :title="template ? `Subject: ${template.subject}` : 'Email Template'"
    :back-to="{ name: RouteNames.AdminEmailTemplates }"
  >
    <template #subtitle>
      <div v-if="template" class="d-flex align-center ga-2">
        <SoChip
          :type="senderChipType(template.sender)"
          :text="template.sender"
        />
        <span>Channel: {{ template.name }}</span>
      </div>
    </template>

    <template #headerRight>
      <div class="d-flex flex-nowrap align-center ga-2">
        <div
          v-if="template"
          class="read-only-note"
          data-testid="read-only-warning"
        >
          <SoIcon
            icon="alert-circle"
            size="small"
            color="caution"
            class="read-only-note__icon"
          />
          <span>{{ readOnlyWarning }}</span>
        </div>
        <EmailTemplateVersionSelect
          v-if="versions.length"
          v-model="selectedVersionId"
          class="version-select"
          :versions="versions"
        />
      </div>
    </template>

    <div
      v-if="query.isFetching && !template"
      class="d-flex justify-center pa-8"
    >
      <SoProgress indeterminate />
    </div>
    <SoAlert
      v-else-if="!template"
      type="info"
      text="Template not found."
      data-testid="no-template-alert"
    />
    <div v-else class="detail-content">
      <SoDivider class="mb-4" />
      <EmailTemplatePreview
        fill-height
        hide-plain-text
        class="template-preview"
        :html-content="template.body"
        :render-debounce-ms="0"
      >
        <template #headerLeft>
          <EmailTemplateValidationSummary
            :findings="validationFindings"
            :assets="templateAssets"
            :variables="templateVariables"
            :links="templateLinks"
            :checking-assets="checkingAssets"
          />
        </template>
        <template #toggleStart>
          <SoButton
            type="text"
            title="Template info"
            data-testid="version-details-button"
            @click="showDetails = true"
          />
          <SoButton
            v-if="mandrillUrl"
            type="text"
            title="Mandrill activity"
            icon="external-link sm"
            :href="mandrillUrl"
            target="_blank"
            data-testid="mandrill-activity"
          />
        </template>
      </EmailTemplatePreview>

      <SoDialog
        v-model="showDetails"
        card-title="Template details"
        :max-width="480"
        data-testid="version-details-dialog"
      >
        <div class="d-flex flex-column ga-4">
          <div>
            <div class="text-caption text-medium-emphasis">Last updated</div>
            <div>
              {{
                template.updatedAt ? formatDateTime(template.updatedAt) : "—"
              }}
            </div>
          </div>
          <div>
            <div class="text-caption text-medium-emphasis">Created at</div>
            <div>
              {{
                template.createdAt ? formatDateTime(template.createdAt) : "—"
              }}
            </div>
          </div>
          <div>
            <div class="text-caption text-medium-emphasis">
              Reply-to address
            </div>
            <div>{{ template.replyToAddress ?? "—" }}</div>
          </div>
        </div>
      </SoDialog>
    </div>
  </SoAdminPage>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import { RouteNames } from "@/types/router"
  import { EmailTemplatesService } from "@/services/admin/EmailTemplatesService"
  import { formatDateTime } from "@/helpers/dateHelper"
  import SoAdminPage from "@/components/templates/SoAdminPage.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoChip from "@/components/SoChip.vue"
  import SoIcon from "@/components/SoIcon.vue"
  import SoDivider from "@/components/SoDivider.vue"
  import SoDialog from "@/components/SoDialog.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import SoProgress from "@/components/SoProgress.vue"
  import EmailTemplateVersionSelect from "./components/EmailTemplateVersionSelect.vue"
  import EmailTemplatePreview from "./components/EmailTemplatePreview.vue"
  import EmailTemplateValidationSummary from "./components/EmailTemplateValidationSummary.vue"
  import { EmailTemplateValidationService } from "@/services/admin/emailTemplateValidation/EmailTemplateValidationService"
  import {
    AssetCheck,
    ValidationFinding,
  } from "@/services/admin/emailTemplateValidation/emailTemplateValidation.types"
  import {
    EmailTemplateSender,
    IEmailTemplateVersionOption,
  } from "@/models/admin/emailTemplates/emailTemplates.types"
  import { IExternalEmailTemplate } from "@/models/admin/emailTemplates/externalEmailTemplates.types"
  import {
    senderChipType,
    senderReadOnlyWarning,
  } from "./helpers/senderPresentation"
  import { mandrillActivityUrl } from "./helpers/mandrillLink"

  export default defineComponent({
    components: {
      SoAdminPage,
      SoButton,
      SoChip,
      SoIcon,
      SoDivider,
      SoDialog,
      SoAlert,
      SoProgress,
      EmailTemplateVersionSelect,
      EmailTemplatePreview,
      EmailTemplateValidationSummary,
    },
    data() {
      return {
        RouteNames,
        query: EmailTemplatesService.getExternalEmailTemplateById(
          Number(this.$route.params.id),
        ),
        selectedVersionId: null as number | null,
        templateAssets: [] as AssetCheck[],
        checkingAssets: false,
        showDetails: false,
      }
    },
    computed: {
      templateId(): number {
        return Number(this.$route.params.id)
      },
      template(): IExternalEmailTemplate | null {
        return this.query.data ?? null
      },
      // Externally-owned templates are mirrored one record per name, so the
      // dropdown always holds the single synced version, which is what sends.
      versions(): IEmailTemplateVersionOption[] {
        if (!this.template) return []
        return [
          {
            id: this.template.id,
            version: this.template.version,
            isLive: true,
            createdAt: this.template.createdAt,
          },
        ]
      },
      readOnlyWarning(): string {
        return senderReadOnlyWarning(
          this.template?.sender ?? EmailTemplateSender.EXTERNAL,
        )
      },
      mandrillUrl(): string | null {
        return mandrillActivityUrl(this.template?.subject ?? null)
      },
      templateVariables(): string[] {
        if (!this.template) return []
        return EmailTemplateValidationService.extractVariables(
          this.template.body,
          "",
        )
      },
      templateLinks(): string[] {
        if (!this.template) return []
        return EmailTemplateValidationService.extractLinks(
          this.template.body,
          "",
        )
      },
      validationFindings(): ValidationFinding[] {
        if (!this.template) return []
        return [
          ...EmailTemplateValidationService.runStaticChecks({
            htmlContent: this.template.body,
            plainContent: "",
          }),
          ...EmailTemplateValidationService.assetFindings(this.templateAssets),
        ]
      },
    },
    watch: {
      template: {
        immediate: true,
        handler(template: IExternalEmailTemplate | null) {
          if (!template) return
          this.selectedVersionId = template.id
          this.templateAssets = []
          this.checkAssets()
        },
      },
      templateId(id: number) {
        this.query = EmailTemplatesService.getExternalEmailTemplateById(id)
      },
    },
    methods: {
      formatDateTime,
      senderChipType,
      // Probing waits on the network per asset, so a template navigated away
      // from mid-probe must not land its results on the one now on screen.
      async checkAssets() {
        const template = this.template
        if (!template) return
        const requestedId = this.templateId
        this.checkingAssets = true
        try {
          const assets = await EmailTemplateValidationService.checkAssets({
            htmlContent: template.body,
            plainContent: "",
          })
          if (this.templateId !== requestedId) return
          this.templateAssets = assets
        } finally {
          if (this.templateId === requestedId) this.checkingAssets = false
        }
      },
    },
  })
</script>

<style scoped>
  .detail-content {
    display: flex;
    flex-direction: column;
    /* Fill the viewport below the nav + page header without overflowing it. */
    height: calc(100vh - 270px);
  }

  .template-preview {
    flex: 1;
    min-height: 0;
  }

  .version-select :deep(.v-input) {
    margin-bottom: 0 !important;
  }

  .read-only-note {
    /* Stretches to whatever height the version dropdown next to it takes. */
    align-self: stretch;
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 0 12px;
    border-radius: var(--theme-radius);
    /* Matches SoAlert's warning variant: tinted fill behind a warning border. */
    border: 1px solid rgb(var(--v-theme-warning));
    background: rgba(var(--v-theme-warning), var(--v-activated-opacity));
    color: rgb(var(--v-theme-on-surface));
    font-size: 0.875rem;
    white-space: nowrap;
  }

  /* SoIcon's wrapper is inline, so the glyph would sit on the text baseline. */
  .read-only-note__icon {
    display: flex;
    align-items: center;
  }
</style>
